import type { CashPool } from '@/types/cash-pool.types';
import { formatCurrencyCode } from '@/utils/trades/mtm-calculations';

// LR-542 (demo feedback #63): the repo cash pool is DISPLAYED in the customer's BILLING
// convention — the sign is flipped relative to storage/API. Stored (ledger) convention,
// unchanged everywhere below the render layer: + = we hold the CP's cash, and
// net_due = total_exposure − cashpool. Displayed (billing) convention: − = we hold the
// CP's cash (we pay interest on it), + = the CP holds ours (the CP pays us). A call due
// to us therefore DECREASES the displayed pool.
//
// The flip is render-only. Every pool figure (cashpool_before / cashpool_after / balance
// cashpool) must pass through displayCashPool() on its way to the screen — rendering the
// raw API value is a sign bug. Net Due / exposure / direction keep their own axis
// (+ = due to us) and are NOT flipped.

/** Ledger-convention pool value (as stored/served) → billing-convention display value. */
export function displayCashPool(stored: number): number {
  // Avoid -0: a flipped zero must still render unsigned ("0 USD", not "-0 USD").
  return stored === 0 ? 0 : -stored;
}

/**
 * Direction caption for a DISPLAYED (billing-signed) pool value — the drawer balance and
 * the History hero. Spells out who holds the collateral and who pays interest on it,
 * matching the customer's rationale for the flip.
 */
export function cashPoolHolderCaption(displayed: number): string {
  if (displayed < 0) return 'we hold CP cash — we pay interest';
  if (displayed > 0) return 'CP holds our cash — CP pays us';
  return 'settled';
}

/** Compact caption for a DISPLAYED pool value — the History "By counterparty" rail rows. */
export function cashPoolRailCaption(displayed: number): string {
  if (displayed < 0) return 'we pay interest';
  if (displayed > 0) return 'CP pays interest';
  return 'settled';
}

/**
 * Explicit "+" on a positive DISPLAYED pool figure. On pool cells the sign IS the information
 * (who holds the collateral / who pays interest), but the compact/exact currency formatters
 * (formatCurrency / formatCurrencyExact) only emit "-" — a bare positive would read as
 * unsigned. Zero stays bare (a settled pool has no direction).
 */
export function withPoolSign(displayed: number, formatted: string): string {
  return displayed > 0 ? `+${formatted}` : formatted;
}

/**
 * The currency a book's ledger balance is in, or null when it has no single one. The balance adds
 * the book's shares across its pools without converting, so it is one figure only while every
 * pool holding it is in one currency; a book holding nothing falls back to `fallback`.
 */
export function bookBalanceCurrency(
  pools: ReadonlyArray<{ currency: string }>,
  fallback: string
): string | null {
  const currencies = new Set(pools.map((pool) => pool.currency));
  if (currencies.size > 1) return null;
  return currencies.size === 1 ? pools[0].currency : fallback;
}

/**
 * Does the book's ledger balance differ from what its last call left (`cashpool_after`)? Only
 * margin calls move a GMRA pool, and a call records the whole book's balance after it, so the two
 * agree unless something moved the pool outside the calls. Both figures in the LEDGER sign; a
 * cent is the tolerance, since both are 2-dp decimal strings.
 */
export function differsFromLastCall(
  ledger: string | null | undefined,
  lastCallAfter: string
): boolean {
  if (ledger == null) return false;
  return (
    Math.round(Number(ledger) * 100) !== Math.round(Number(lastCallAfter) * 100)
  );
}

export const LAST_CALL_MISMATCH = {
  label: 'differs from last call',
  reason:
    'The pool ledger moved outside margin calls since the last one — for example, calls ' +
    'posted before the pool ledger existed, or a manual correction.',
} as const;

/**
 * What a Run MTM moved in the fee pools, for the success toast: one figure per pool that moved,
 * in the server's order; `''` when none did. Per pool, not per currency: a counterparty may hold
 * two pools in one currency, and +1,000 in one with −1,000 in the other must not read as nothing.
 * LEDGER sign, not `displayCashPool`: the GMSLA balance renders unflipped in the pool strip and
 * the register.
 */
export function describePoolPostings(
  postings: ReadonlyArray<{ currency: string; amount: string }>
): string {
  const moved = postings.filter(({ amount }) => Number(amount) !== 0);
  if (moved.length === 0) return '';
  const figures = moved.map(({ currency, amount }) =>
    formatCurrencyCode(Number(amount), currency)
  );
  return `${moved.length === 1 ? 'fee pool' : 'fee pools'} ${figures.join(', ')}`;
}

/**
 * A pool as offered for picking: currency and rate first, the name second. The name is free text
 * and can read as another counterparty's pool, so it must not be what tells pools apart.
 */
export function cashPoolOptionLabel(pool: Pick<CashPool, 'currency' | 'rate_definition' | 'name'>): string {
  return [pool.currency, pool.rate_definition, pool.name].filter(Boolean).join(' · ');
}
